import React, { useEffect, useState, useMemo } from 'react';
import EnhancedTable from '@components/_old/table/EnhancedTable';
import Loader from '@components/loader/Loader';
import { getModelHeaders } from '../content';
import ModelDrawer from '../../model-drawer/ModelDrawer';
import SelectFormField from '@components/form-fields/SelectFormField';
import { supervisorApiBaseUrl } from '@utils/env-utils';
import { mapToModelsListingData } from '../utils';

const BASE_URL = supervisorApiBaseUrl;
const DEFAULT_BRAND = 'Brand01';

const ModelsTable: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editRow, setEditRow] = useState<any | null>(null);
  const [isEdit, setIsEdit] = useState<any | null>(null);

  const [brandLovs, setBrandLovs] = useState<Record<string, string>>({});
  const [brandFilter, setBrandFilter] = useState<string>(DEFAULT_BRAND);

  useEffect(() => {
    fetch(`${BASE_URL}/api/Lookups/Insurance-Brands`)
      .then((res) => res.json())
      .then((brandListFromApi) => {
        const brandsObject: Record<string, string> = {};
        Object.values(brandListFromApi).forEach((brand: any) => {
          if (brand?.Code && brand?.Title) {
            brandsObject[brand.Code] = `${brand.Code} - ${brand.Title}`;
          }
        });
        setBrandLovs(brandsObject);
      });
  }, []);

  const loadData = (brandCode?: string) => {
    setLoading(true);

    const url = brandCode
      ? `${BASE_URL}/api/Lookups/Insurance-Brands/${brandCode}/relatedLookups/Insurance-Models`
      : `${BASE_URL}/api/Lookups/Insurance-Models`;

    fetch(url)
      .then((res) => res.json())
      .then((result) => {
        const rows = Array.isArray(result)
          ? result
          : Object.values(result || {});
        setData(rows);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData(brandFilter);
    setPageNumber(0);
  }, [brandFilter]);

  const tableData = useMemo(
    () => ({
      ...mapToModelsListingData(data, brandLovs),
      pageNumber,
      pageSize,
    }),
    [data, brandLovs, pageNumber, pageSize]
  );

  const existingCodes = data
    .filter((b) => !editRow || b.Code !== editRow.Code)
    .map((b) => b.Code);

  const headers = getModelHeaders(
    () => {
      setEditRow(null);
      setDrawerOpen(true);
    },
    (row: any) => {
      setIsEdit(true);
      setEditRow(row._original); // pull original raw item
      setDrawerOpen(true);
    }
  );

  const actions = [
    {
      title: '+ New',
      onClick: () => {
        setEditRow({ brand: brandFilter });
        setIsEdit(false);
        setDrawerOpen(true);
      },
      isEntity: false,
      isBulk: false,
      iconUrl: '',
      hidden: false,
      disabled: false,
    },
  ];

  const handlePageChange = (page: number) => setPageNumber(page);
  const handleRowsPerPageChange = (size: number) => {
    setPageSize(size);
    setPageNumber(0);
  };

  const lineNameController = (
    <div
      style={{
        minWidth: 150,
        maxWidth: 150,
        paddingTop: '42px',
        paddingRight: '10px',
      }}
    >
      <SelectFormField
        name="brandFilter"
        title=""
        value={brandFilter}
        placeholder="All Brands"
        selectOptions={brandLovs}
        onChange={(v: string) => {
          setPageNumber(0);
          setBrandFilter(v);
        }}
        canClearValue={false}
        material
      />
    </div>
  );

  return loading ? (
    <Loader />
  ) : (
    <>
      <EnhancedTable
        lineNameController={lineNameController}
        title={null}
        inlineTitle="Values"
        name="models"
        entityName="Model"
        entityIdColumnName="Id"
        orderByAscendingByDefault
        defaultOrderByColumn="Code"
        inline
        data={tableData}
        headers={headers}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
        currentPage={pageNumber}
        usePagination
        disableSelection
        hideToolbar={false}
        actions={actions}
        loader={loading}
        showInlineFilter={false}
        rowsPerPageOptions={[5, 10, 25, 100]}
      />
      {drawerOpen && (
        <ModelDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSuccess={() => {
            setDrawerOpen(false);
            setEditRow(null);
            loadData(brandFilter);
          }}
          existingCodes={existingCodes}
          brandLovs={brandLovs as any}
          model={editRow}
          isEdit={isEdit}
        />
      )}
    </>
  );
};

export default ModelsTable;
